<template>
  <tr class="hover:bg-beige-0 transition-background border-beige-10 border-b duration-200">
    <td class="whitespace-nowrap">{{ formattedDate }}</td>
    <td>
      <RfColorTag
        backgroundColor="#E0EDF9"
        :title="audit.auditable_type"
        size="small"
        pill
        disabled
      />
    </td>
    <td>{{ audit.auditable_name || audit.auditable_id }}</td>
    <td>{{ audit.auditable_id }}</td>
    <td>
      <div>{{ associatedLabel }}</div>
      <div v-if="audit.associated_name && hasAssociatedId" class="text-xs text-gray-500">
        ID: {{ audit.associated_id }}
      </div>
    </td>
    <td>
      <RfColorTag :backgroundColor="actionColor" :title="audit.action" size="small" pill disabled />
    </td>
    <td>
      <div>{{ audit.user_name }}</div>
      <div class="text-xs text-gray-500">{{ audit.user_email }}</div>
    </td>
    <td>
      <div class="text-sm">
        {{ changesText }}
      </div>
    </td>
    <td>
      <RfButtonNew type="tertiary" size="small" @click="emit('view', audit)"> View </RfButtonNew>
    </td>
  </tr>
</template>

<script setup>
import { computed } from "vue";
import dayjs from "dayjs";
import StringUtils from "@/utils/StringUtils";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import RfButtonNew from "@/components/buttons/RfButtonNew.vue";
import { countObjectKeyChanges, isKeyDiffablePair, toChangePair } from "@/utils/jsonDiffUtils";

const props = defineProps({
  audit: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["view"]);

const formattedDate = computed(() => {
  return dayjs(props.audit.created_at).format("MMM DD, YYYY h:mm A");
});

const hasAssociatedId = computed(() => {
  const id = props.audit.associated_id;
  return id !== null && id !== undefined && id !== "";
});

const associatedLabel = computed(() => {
  if (props.audit.associated_name) return props.audit.associated_name;
  return hasAssociatedId.value ? `ID: ${props.audit.associated_id}` : "—";
});

const actionColor = computed(() => {
  switch (props.audit.action) {
    case "create":
      return "#DFF1BE"; // Light green for create
    case "update":
      return "#E0EDF9"; // Light blue for update
    case "destroy":
      return "#FFDDEF"; // Light pink for destroy
    default:
      return "#EDEBE4"; // Light gray for default
  }
});

const changesCount = computed(() => {
  return Object.values(props.audit.audited_changes || {}).reduce((total, values) => {
    const [before, after] = toChangePair(values);
    if (isKeyDiffablePair(before, after)) {
      return total + countObjectKeyChanges(before, after);
    }
    return total + 1;
  }, 0);
});

const hasKeyDiffField = computed(() => {
  return Object.values(props.audit.audited_changes || {}).some(values => {
    const [before, after] = toChangePair(values);
    return isKeyDiffablePair(before, after);
  });
});

const changesText = computed(() => {
  const count = changesCount.value;
  if (!hasKeyDiffField.value) {
    return `${count} ${StringUtils.pluralize("field", count)} changed`;
  }

  const fieldCount = Object.keys(props.audit.audited_changes || {}).length;
  return `${count} ${StringUtils.pluralize("change", count)} across ${fieldCount} ${StringUtils.pluralize("field", fieldCount)}`;
});
</script>
